import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { DeleteIcon } from "../../../icons/Delete";
import { CameraIcon } from "../../../icons/Camera";
import { useFocusTrap } from "../../../hook/useFocusTrap";
import { REGISTRATION_KEYBOARD_SCOPE_ATTR } from "../../RegistrationLayout/registrationFocusStyles";
import {
  RegistrationFieldError,
} from "../../RegistrationLayout/RegistrationFieldError";
import {
  RegistrationRequiredMark,
} from "../../RegistrationLayout/RegistrationA11y";
import {
  hasRequiredInsuranceCardImages,
  hydrateInsuranceCardImagePreviews,
  setInsuranceCardImages,
} from "./insuranceCardImagesStore";

const MAX_CARD_IMAGE_BYTES = 10 * 1024 * 1024;
const MIN_CARD_IMAGE_SIZE = 100;

const RegistrationInsuranceCardUpload = ({
  forceShowErrors = false,
  idPrefix = "insurance-card",
  onImagesChange,
}) => {
  const { t } = useTranslation();
  const frontGalleryInputRef = useRef(null);
  const backGalleryInputRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const sourceSheetRef = useRef(null);
  const cameraPanelRef = useRef(null);
  const [cardImages, setCardImages] = useState(() =>
    hydrateInsuranceCardImagePreviews()
  );
  const [uploadSourceSide, setUploadSourceSide] = useState(null);
  const [cameraCaptureSide, setCameraCaptureSide] = useState(null);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [cardImagesTouched, setCardImagesTouched] = useState(false);

  useEffect(() => {
    setCardImages(hydrateInsuranceCardImagePreviews());
  }, []);

  const notifyImagesChange = (images) => {
    if (typeof onImagesChange === "function") {
      onImagesChange(hasRequiredInsuranceCardImages(images));
    }
  };

  const updateCardImages = (next) => {
    setCardImages(next);
    setInsuranceCardImages(next);
    notifyImagesChange(next);
  };

  const applyCapturedCardFile = (side, file) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.src = objectUrl;
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      if (width < MIN_CARD_IMAGE_SIZE || height < MIN_CARD_IMAGE_SIZE) {
        URL.revokeObjectURL(objectUrl);
        message.error(t("IMAGE_TYPE_VALIDATION"));
        return;
      }

      const previewKey = side === "front" ? "frontPreview" : "backPreview";
      const fileKey = side === "front" ? "frontFile" : "backFile";
      setCardImages((prev) => {
        if (prev[previewKey]) {
          URL.revokeObjectURL(prev[previewKey]);
        }
        const next = {
          ...prev,
          [fileKey]: file,
          [previewKey]: objectUrl,
        };
        setInsuranceCardImages(next);
        notifyImagesChange(next);
        return next;
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      message.error(t("IMAGE_TYPE_VALIDATION"));
    };
  };

  const handleCardImageChange = (side, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    if (file.size > MAX_CARD_IMAGE_BYTES) {
      message.error(t("IMAGE_SIZE_VALIDATION"));
      return;
    }

    const hasAllowedExtension = /\.(jpg|jpeg|png|gif|heif|heic|webp)$/i.test(
      file.name || ""
    );
    const hasAllowedMimeType = String(file.type || "")
      .toLowerCase()
      .startsWith("image/");
    if (!hasAllowedExtension && !hasAllowedMimeType) {
      message.error(t("IMAGE_TYPE_VALIDATION"));
      return;
    }

    applyCapturedCardFile(side, file);
  };

  const stopCameraStream = () => {
    const stream = cameraStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const closeCameraCapture = () => {
    stopCameraStream();
    setIsCameraStarting(false);
    setCameraCaptureSide(null);
  };

  const handleRemoveCardImage = (side, event) => {
    event.stopPropagation();
    const previewKey = side === "front" ? "frontPreview" : "backPreview";
    const fileKey = side === "front" ? "frontFile" : "backFile";
    if (cardImages[previewKey]) {
      URL.revokeObjectURL(cardImages[previewKey]);
    }
    updateCardImages({
      ...cardImages,
      [fileKey]: null,
      [previewKey]: null,
    });
  };

  const openUploadSourceChooser = (side) => {
    setCardImagesTouched(true);
    setUploadSourceSide(side);
  };

  const closeUploadSourceChooser = () => {
    setUploadSourceSide(null);
  };

  useFocusTrap({
    isOpen: Boolean(uploadSourceSide),
    containerRef: sourceSheetRef,
    onEscape: closeUploadSourceChooser,
  });

  useFocusTrap({
    isOpen: Boolean(cameraCaptureSide),
    containerRef: cameraPanelRef,
    onEscape: closeCameraCapture,
  });

  const handleUploadFromCamera = () => {
    const side = uploadSourceSide;
    if (!side) {
      return;
    }

    closeUploadSourceChooser();

    if (!navigator?.mediaDevices?.getUserMedia) {
      message.error(
        t("NUTRITION_INSDISC_ERR_CAMERA_UNSUPPORTED", {
          defaultValue: "Camera is not supported on this device.",
        })
      );
      return;
    }

    setCameraCaptureSide(side);
  };

  const handleCaptureCameraPhoto = () => {
    const side = cameraCaptureSide;
    const video = cameraVideoRef.current;
    if (!side || !video || !video.videoWidth || !video.videoHeight) {
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          message.error(t("IMAGE_TYPE_VALIDATION"));
          return;
        }
        if (blob.size > MAX_CARD_IMAGE_BYTES) {
          message.error(t("IMAGE_SIZE_VALIDATION"));
          return;
        }

        const file = new File(
          [blob],
          `insurance-card-${side}-${Date.now()}.jpg`,
          { type: "image/jpeg" }
        );
        applyCapturedCardFile(side, file);
        closeCameraCapture();
      },
      "image/jpeg",
      0.92
    );
  };

  const handleUploadFromGallery = () => {
    const side = uploadSourceSide;
    if (!side) {
      return;
    }
    const inputRef =
      side === "front" ? frontGalleryInputRef : backGalleryInputRef;
    inputRef.current?.click();
    closeUploadSourceChooser();
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  useEffect(() => {
    if (!cameraCaptureSide) {
      return undefined;
    }

    let cancelled = false;

    const startCamera = async () => {
      setIsCameraStarting(true);
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        cameraStreamRef.current = stream;
        if (cameraVideoRef.current) {
          cameraVideoRef.current.srcObject = stream;
          await cameraVideoRef.current.play();
        }
      } catch (err) {
        if (!cancelled) {
          closeCameraCapture();
          message.error(
            t("NUTRITION_INSDISC_ERR_CAMERA_PERMISSION", {
              defaultValue:
                "Unable to access camera. Please allow camera permission and try again.",
            })
          );
        }
      } finally {
        if (!cancelled) {
          setIsCameraStarting(false);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [cameraCaptureSide, t]);

  const showCardImagesError = forceShowErrors || cardImagesTouched;
  const missingRequiredImages =
    !(cardImages.frontFile instanceof File) ||
    !(cardImages.backFile instanceof File);
  const cardImagesErrorId = `${idPrefix}-card-images-error`;

  return (
    <>
      <fieldset
        className="form-group insurance-details-span-2 insurance-card-upload"
        aria-required="true"
        aria-invalid={showCardImagesError && missingRequiredImages ? true : undefined}
        aria-describedby={
          showCardImagesError && missingRequiredImages
            ? cardImagesErrorId
            : undefined
        }
      >
        <legend className="insurance-card-upload-title">
          {t("MY_PROFILE_LBL_UPLOAD_IMAGE")} <RegistrationRequiredMark />
        </legend>
        <p className="insurance-card-upload-desc">
          {t("MY_PROFILE_LBL_UPLOAD_IMAGE_FRONT_AND_BACK_OF_INSURANCE_CARD")}
        </p>
        <input
          ref={frontGalleryInputRef}
          id={`${idPrefix}-front-gallery`}
          type="file"
          accept="image/png, image/jpeg, image/jpg, image/gif, image/heif, image/*"
          style={{ display: "none" }}
          onChange={(event) => handleCardImageChange("front", event)}
        />
        <input
          ref={backGalleryInputRef}
          id={`${idPrefix}-back-gallery`}
          type="file"
          accept="image/png, image/jpeg, image/jpg, image/gif, image/heif, image/*"
          style={{ display: "none" }}
          onChange={(event) => handleCardImageChange("back", event)}
        />
        <div className="insurance-card-upload-grid">
          <div className="insurance-card-upload-slot">
            <div className="insurance-card-upload-box">
              {cardImages.frontPreview ? (
                <>
                  <button
                    type="button"
                    className="insurance-card-upload-replace"
                    id={`${idPrefix}-front-upload`}
                    onClick={() => openUploadSourceChooser("front")}
                    aria-label={`${t("MY_PROFILE_LBL_UPLOAD_IMAGE")} ${t(
                      "MY_PROFILE_LBL_FRONT"
                    )}`}
                  >
                    <img
                      src={cardImages.frontPreview}
                      alt={t("MY_PROFILE_LBL_FRONT")}
                      className="insurance-card-upload-preview"
                    />
                  </button>
                  <button
                    type="button"
                    className="insurance-card-upload-delete"
                    aria-label={t("NUTRITION_INSDISC_ARIA_REMOVE_CARD_FRONT", {
                      defaultValue: `${t("NUTRITION_COMMON_BTN_REMOVE", {
                        defaultValue: "Remove",
                      })} ${t("MY_PROFILE_LBL_FRONT")}`,
                    })}
                    onClick={(event) => handleRemoveCardImage("front", event)}
                  >
                    <DeleteIcon aria-hidden />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="insurance-card-upload-empty"
                  id={`${idPrefix}-front-upload`}
                  onClick={() => openUploadSourceChooser("front")}
                  aria-label={`${t("MY_PROFILE_LBL_UPLOAD_IMAGE")} ${t(
                    "MY_PROFILE_LBL_FRONT"
                  )}`}
                >
                  <span
                    className="insurance-card-upload-placeholder"
                    aria-hidden
                  >
                    <CameraIcon />
                  </span>
                </button>
              )}
            </div>
            <div className="insurance-card-upload-label">
              {t("MY_PROFILE_LBL_FRONT")}
            </div>
          </div>
          <div className="insurance-card-upload-slot">
            <div className="insurance-card-upload-box">
              {cardImages.backPreview ? (
                <>
                  <button
                    type="button"
                    className="insurance-card-upload-replace"
                    id={`${idPrefix}-back-upload`}
                    onClick={() => openUploadSourceChooser("back")}
                    aria-label={`${t("MY_PROFILE_LBL_UPLOAD_IMAGE")} ${t(
                      "MY_PROFILE_LBL_BACK"
                    )}`}
                  >
                    <img
                      src={cardImages.backPreview}
                      alt={t("MY_PROFILE_LBL_BACK")}
                      className="insurance-card-upload-preview"
                    />
                  </button>
                  <button
                    type="button"
                    className="insurance-card-upload-delete"
                    aria-label={t("NUTRITION_INSDISC_ARIA_REMOVE_CARD_BACK", {
                      defaultValue: `${t("NUTRITION_COMMON_BTN_REMOVE", {
                        defaultValue: "Remove",
                      })} ${t("MY_PROFILE_LBL_BACK")}`,
                    })}
                    onClick={(event) => handleRemoveCardImage("back", event)}
                  >
                    <DeleteIcon aria-hidden />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="insurance-card-upload-empty"
                  id={`${idPrefix}-back-upload`}
                  onClick={() => openUploadSourceChooser("back")}
                  aria-label={`${t("MY_PROFILE_LBL_UPLOAD_IMAGE")} ${t(
                    "MY_PROFILE_LBL_BACK"
                  )}`}
                >
                  <span
                    className="insurance-card-upload-placeholder"
                    aria-hidden
                  >
                    <CameraIcon />
                  </span>
                </button>
              )}
            </div>
            <div className="insurance-card-upload-label">
              {t("MY_PROFILE_LBL_BACK")}
            </div>
          </div>
        </div>
        {showCardImagesError && missingRequiredImages ? (
          <RegistrationFieldError
            id={cardImagesErrorId}
            style={{ marginTop: 10 }}
          >
            {t("MY_PROFILE_LBL_UPLOAD_IMAGE_FRONT_AND_BACK_OF_INSURANCE_CARD")}
          </RegistrationFieldError>
        ) : null}
      </fieldset>

      {uploadSourceSide ? (
        <div
          className="insurance-upload-source-overlay"
          role="presentation"
          onClick={closeUploadSourceChooser}
          {...REGISTRATION_KEYBOARD_SCOPE_ATTR}
        >
          <div
            ref={sourceSheetRef}
            className="insurance-upload-source-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${idPrefix}-upload-source-title`}
            onClick={(event) => event.stopPropagation()}
          >
            <div
              id={`${idPrefix}-upload-source-title`}
              className="insurance-upload-source-title"
            >
              {t("VIEW_SELECT_FOOD_TRACK_TXT_SELECT_OPTION")}
            </div>
            <button
              type="button"
              className="insurance-upload-source-option"
              onClick={handleUploadFromCamera}
            >
              {t("ADD_FOOD_TRACK_BTN_MEAL_PHOTO_CAMERA")}
            </button>
            <button
              type="button"
              className="insurance-upload-source-option"
              onClick={handleUploadFromGallery}
            >
              {t("ADD_FOOD_TRACK_BTN_GALLERY")}
            </button>
            <button
              type="button"
              className="insurance-upload-source-cancel"
              onClick={closeUploadSourceChooser}
            >
              {t("ADD_FOOD_TRACK_BTN_MEAL_PHOTO_CANEL")}
            </button>
          </div>
        </div>
      ) : null}

      {cameraCaptureSide ? (
        <div
          className="insurance-camera-overlay"
          role="presentation"
          {...REGISTRATION_KEYBOARD_SCOPE_ATTR}
        >
          <div
            className="insurance-camera-panel"
            ref={cameraPanelRef}
            role="dialog"
            aria-modal="true"
            aria-label={t("ADD_FOOD_TRACK_BTN_MEAL_PHOTO_CAMERA")}
          >
            <div className="insurance-camera-preview-wrap">
              {isCameraStarting ? (
                <div className="insurance-camera-loading">
                  {t("NUTRITION_COMMON_LBL_LOADING", {
                    defaultValue: "Loading...",
                  })}
                </div>
              ) : null}
              <video
                ref={cameraVideoRef}
                className="insurance-camera-video"
                playsInline
                muted
                autoPlay
              />
            </div>
            <div className="insurance-camera-actions">
              <button
                type="button"
                className="insurance-upload-source-cancel"
                onClick={closeCameraCapture}
              >
                {t("ADD_FOOD_TRACK_BTN_MEAL_PHOTO_CANEL")}
              </button>
              <button
                type="button"
                className="btn-primary insurance-camera-capture-btn"
                onClick={handleCaptureCameraPhoto}
                disabled={isCameraStarting}
              >
                {t("ADD_FOOD_TRACK_BTN_MEAL_PHOTO_CAMERA")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export { hasRequiredInsuranceCardImages };
export default RegistrationInsuranceCardUpload;
